import { AdminRoles, DoctorRoles, Role } from './DiactiveTypes';
import { formatDateTime } from '../utils/DiactiveUtils';
import { getCachedHospitals, getHospitalName, getHospitalShortName } from '../services/HospitalService';
import { validateEmail, validateName } from '../utils/ValidationUtils';
import { addUser, deleteUser, getAllUsersByRole, getUserById, updateUser } from '../services/UserService';
import SentMailTable from '../components/patients/SentMailTable';

const compareTimestamps = (a, b) => {
  const aMs = a?.toDate ? a.toDate().getTime() : 0;
  const bMs = b?.toDate ? b.toDate().getTime() : 0;
  return aMs - bMs;
};

const compareNames = (a, b) =>
  (a ?? '').localeCompare(b ?? '', 'es', { sensitivity: 'base' });

const safeHospitalName = (hospitalId) => {
  if (!hospitalId) return '—';
  try {
    return getHospitalName(hospitalId);
  } catch {
    return '—';
  }
};

const doctorConfig = {
  key: Role.Doctor,
  roles: DoctorRoles,
  defaultRole: Role.Doctor,

  labels: {
    singular: 'médico',
    plural: 'Médicos',
    addTitle: 'Añadir médico',
    editTitle: 'Editar médico',
    addButton: 'Añadir médico',
    createSubmit: 'Crear médico',
    dataTab: 'Datos del médico',
    emptyState: 'No hay médicos',
    notFound: (id) => `No se ha encontrado el médico ${id}`,
    invalid: (id) => `El usuario ${id} no es un médico válido`,
    loadError: 'Médico no válido',
    backToList: 'Volver a médicos',
    deleteTitle: 'Eliminar médico',
    deleteDescription:
      '¿Seguro que quieres eliminar este médico de forma permanente? Esta acción no se puede deshacer y borrará también su cuenta de acceso.',
  },

  routes: {
    list: () => '/doctors/',
    view: (id, tab) => `/doctor/view/${id}${tab ? `/${tab}` : ''}`,
    add: () => '/doctor/add',
    edit: (id) => `/doctor/edit/${id}`,
  },

  service: {
    getAll: () => getAllUsersByRole(Role.Doctor),
    getById: getUserById,
    add: (payload) => addUser(Role.Doctor, payload),
    update: (userId, payload) => updateUser(Role.Doctor, userId, payload),
    remove: (userId) => deleteUser(Role.Doctor, userId),
  },

  get formFields() {
    return [
      { name: 'firstName', label: 'Nombre', type: 'text', required: true },
      { name: 'lastName', label: 'Apellidos', type: 'text', required: true },
      { name: 'email', label: 'Correo electrónico', type: 'text', required: true },
      {
        name: 'confirmEmail',
        label: 'Confirmar correo electrónico',
        type: 'text',
        required: true,
        excludeFromPayload: true,
        initFrom: 'email',
      },
      {
        name: 'hospital',
        label: 'Hospital',
        type: 'select',
        required: true,
        options: getCachedHospitals().map((h) => ({ value: h.id, label: h.name })),
      },
    ];
  },

  validate: (formData, errors) => {
    validateName(formData.firstName.trim(), formData.lastName.trim(), errors);
    validateEmail(formData.email.trim(), formData.confirmEmail.trim(), errors);
  },

  detailRows: [
    [
      { label: 'Nombre', get: (u) => u.fullName || '—' },
      { label: 'Correo electrónico', get: (u) => u.email || '—' },
      { label: 'Hospital', get: (u) => safeHospitalName(u.hospital) },
    ],
    [
      { label: 'Fecha de alta', get: (u) => formatDateTime(u.createdAt) },
      { label: 'Creado por', get: (u) => u.createdByName || '—' },
    ],
  ],

  extraTabs: [
    {
      id: 'mail',
      label: 'Correos',
      render: (user, ctx) => (
        <SentMailTable email={user.email} canResolve={ctx.canResolve} />
      ),
    },
  ],

  get table() {
    return {
      defaultSort: { key: 'createdAt', direction: 'desc' },
      columns: [
        {
          key: 'createdAt',
          header: 'Fecha de alta',
          width: 220,
          sortable: true,
          defaultDir: 'desc',
          sort: (a, b) => compareTimestamps(a.createdAt, b.createdAt),
          render: (u) => formatDateTime(u.createdAt),
        },
        {
          key: 'name',
          header: 'Nombre',
          width: 320,
          sortable: true,
          defaultDir: 'asc',
          sort: (a, b) => compareNames(a.fullName, b.fullName),
          render: (u) => u.fullName || '—',
          tooltip: (u) => u.fullName || '',
        },
        {
          key: 'hospital',
          header: 'Hospital',
          width: 220,
          filter: {
            options: getCachedHospitals().map((h) => ({ value: h.id, label: h.name })),
            match: (u, v) => !v || u.hospital === v,
            countBy: (u) => u.hospital,
          },
          render: (u) => getHospitalShortName(u.hospital) || '—',
          tooltip: (u) => (u.hospital ? getHospitalName(u.hospital) : ''),
        },
      ],
    };
  },

  permissions: {
    canCreate: (role) => AdminRoles.includes(role),
    canEditOrDelete: (role) => AdminRoles.includes(role),
  },
};

const superUserConfig = {
  key: Role.SuperUser,
  roles: [Role.SuperUser],
  defaultRole: Role.SuperUser,

  labels: {
    singular: 'administrador',
    plural: 'Administradores',
    addTitle: 'Añadir administrador',
    editTitle: 'Editar administrador',
    addButton: 'Añadir administrador',
    createSubmit: 'Crear administrador',
    dataTab: 'Datos del administrador',
    emptyState: 'No hay administradores',
    notFound: (id) => `No se ha encontrado el administrador ${id}`,
    invalid: (id) => `El usuario ${id} no es un administrador válido`,
    loadError: 'Administrador no válido',
    backToList: 'Volver a administradores',
    deleteTitle: 'Eliminar administrador',
    deleteDescription:
      '¿Seguro que quieres eliminar este administrador de forma permanente? Esta acción no se puede deshacer y borrará también su cuenta de acceso.',
  },

  routes: {
    list: () => '/admins/',
    view: (id, tab) => `/admin/view/${id}${tab ? `/${tab}` : ''}`,
    add: () => '/admin/add',
    edit: (id) => `/admin/edit/${id}`,
  },

  service: {
    getAll: () => getAllUsersByRole(Role.SuperUser),
    getById: getUserById,
    add: (payload) => addUser(Role.SuperUser, payload),
    update: (userId, payload) => updateUser(Role.SuperUser, userId, payload),
    remove: (userId) => deleteUser(Role.SuperUser, userId),
  },

  formFields: [
    { name: 'firstName', label: 'Nombre', type: 'text', required: true },
    { name: 'lastName', label: 'Apellidos', type: 'text', required: true },
    { name: 'email', label: 'Correo electrónico', type: 'text', required: true },
    {
      name: 'confirmEmail',
      label: 'Confirmar correo electrónico',
      type: 'text',
      required: true,
      excludeFromPayload: true,
      initFrom: 'email',
    },
  ],

  validate: (formData, errors) => {
    validateName(formData.firstName.trim(), formData.lastName.trim(), errors);
    validateEmail(formData.email.trim(), formData.confirmEmail.trim(), errors);
  },

  detailRows: [
    [
      { label: 'Nombre', get: (u) => u.fullName || '—' },
      { label: 'Correo electrónico', get: (u) => u.email || '—' },
    ],
    [
      { label: 'Fecha de alta', get: (u) => formatDateTime(u.createdAt) },
      { label: 'Creado por', get: (u) => u.createdByName || '—' },
    ],
  ],

  extraTabs: [
    {
      id: 'mail',
      label: 'Correos',
      render: (user, ctx) => (
        <SentMailTable email={user.email} canResolve={ctx.canResolve} />
      ),
    },
  ],

  table: {
    defaultSort: { key: 'createdAt', direction: 'desc' },
    columns: [
      {
        key: 'createdAt',
        header: 'Fecha de alta',
        width: 220,
        sortable: true,
        defaultDir: 'desc',
        sort: (a, b) => compareTimestamps(a.createdAt, b.createdAt),
        render: (u) => formatDateTime(u.createdAt),
      },
      {
        key: 'name',
        header: 'Nombre',
        width: 260,
        sortable: true,
        defaultDir: 'asc',
        sort: (a, b) => compareNames(a.fullName, b.fullName),
        render: (u) => u.fullName || '—',
        tooltip: (u) => u.fullName || '',
      },
      {
        key: 'email',
        header: 'Correo electrónico',
        width: 280,
        render: (u) => u.email || '—',
        tooltip: (u) => u.email || '',
      },
    ],
  },

  permissions: {
    canCreate: (role) => role === Role.Admin,
    canEditOrDelete: (role) => role === Role.Admin,
  },
};

const AppTesterArrowType = {
  FreeStyle: 'FreeStyle',
  Medtronic: 'Medtronic',
};

const inferAppTesterArrowType = (user) => {
  if (!user) return AppTesterArrowType.FreeStyle;
  if (user.cgmType === AppTesterArrowType.FreeStyle && user.usesCgm === true) {
    return AppTesterArrowType.FreeStyle;
  }
  if (user.insulinPumpType === 'Medtronic 780 g' && user.usesCgm === false) {
    return AppTesterArrowType.Medtronic;
  }
  return AppTesterArrowType.FreeStyle;
};

const appTesterArrowTypeToFields = (arrowType) => {
  if (arrowType === AppTesterArrowType.Medtronic) {
    return { cgmType: '', insulinPumpType: 'Medtronic 780 g', usesCgm: false };
  }
  return {
    cgmType: AppTesterArrowType.FreeStyle,
    insulinPumpType: '',
    usesCgm: true,
  };
};

const appTesterConfig = {
  key: Role.AppTester,
  roles: [Role.AppTester],
  defaultRole: Role.AppTester,

  labels: {
    singular: 'tester de app',
    plural: 'Testers de app',
    addTitle: 'Añadir tester de app',
    editTitle: 'Editar tester de app',
    addButton: 'Añadir tester de app',
    createSubmit: 'Crear tester de app',
    dataTab: 'Datos del tester de app',
    emptyState: 'No hay testers de app',
    notFound: (id) => `No se ha encontrado el tester de app ${id}`,
    invalid: (id) => `El usuario ${id} no es un tester de app válido`,
    loadError: 'Tester de app no válido',
    backToList: 'Volver a testers de app',
    deleteTitle: 'Eliminar tester de app',
    deleteDescription:
      '¿Seguro que quieres eliminar este tester de app de forma permanente? Esta acción no se puede deshacer y borrará también su cuenta de acceso.',
  },

  routes: {
    list: () => '/app-testers/',
    view: (id, tab) => `/app-tester/view/${id}${tab ? `/${tab}` : ''}`,
    add: () => '/app-tester/add',
    edit: (id) => `/app-tester/edit/${id}`,
  },

  service: {
    getAll: () => getAllUsersByRole(Role.AppTester),
    getById: getUserById,
    add: (payload) => addUser(Role.AppTester, payload),
    update: (userId, payload) => updateUser(Role.AppTester, userId, payload),
    remove: (userId) => deleteUser(Role.AppTester, userId),
  },

  formFields: [
    { name: 'firstName', label: 'Nombre', type: 'text', required: true },
    { name: 'lastName', label: 'Apellidos', type: 'text', required: true },
    { name: 'email', label: 'Correo electrónico', type: 'text', required: true },
    {
      name: 'confirmEmail',
      label: 'Confirmar correo electrónico',
      type: 'text',
      required: true,
      excludeFromPayload: true,
      initFrom: 'email',
    },
    {
      name: 'tipoDeFlecha',
      label: 'Tipo de flecha',
      type: 'select',
      required: true,
      excludeFromPayload: true,
      options: [
        { value: AppTesterArrowType.FreeStyle, label: AppTesterArrowType.FreeStyle },
        { value: AppTesterArrowType.Medtronic, label: AppTesterArrowType.Medtronic },
      ],
      defaultValue: AppTesterArrowType.FreeStyle,
      getInitialValue: inferAppTesterArrowType,
    },
    {
      name: 'polarRequired',
      label: 'Polar obligatorio',
      type: 'select',
      options: [
        { value: true, label: 'Sí' },
        { value: false, label: 'No' },
      ],
      defaultValue: false,
    },
  ],

  applyPayloadExtras: (formData) =>
    appTesterArrowTypeToFields(formData.tipoDeFlecha ?? AppTesterArrowType.FreeStyle),

  validate: (formData, errors) => {
    validateName(formData.firstName.trim(), formData.lastName.trim(), errors);
    validateEmail(formData.email.trim(), formData.confirmEmail.trim(), errors);
  },

  detailRows: [
    [
      { label: 'Nombre', get: (u) => u.fullName || '—' },
      { label: 'Correo electrónico', get: (u) => u.email || '—' },
      { label: 'Tipo de flecha', get: (u) => inferAppTesterArrowType(u) },
    ],
    [
      { label: 'Fecha de alta', get: (u) => formatDateTime(u.createdAt) },
      { label: 'Creado por', get: (u) => u.createdByName || '—' },
      { label: 'Polar obligatorio', get: (u) => (u.polarRequired ? 'Sí' : 'No') },
    ],
  ],

  extraTabs: [
    {
      id: 'mail',
      label: 'Correos',
      render: (user, ctx) => (
        <SentMailTable email={user.email} canResolve={ctx.canResolve} />
      ),
    },
  ],

  table: {
    defaultSort: { key: 'createdAt', direction: 'desc' },
    columns: [
      {
        key: 'createdAt',
        header: 'Fecha de alta',
        width: 220,
        sortable: true,
        defaultDir: 'desc',
        sort: (a, b) => compareTimestamps(a.createdAt, b.createdAt),
        render: (u) => formatDateTime(u.createdAt),
      },
      {
        key: 'name',
        header: 'Nombre',
        width: 260,
        sortable: true,
        defaultDir: 'asc',
        sort: (a, b) => compareNames(a.fullName, b.fullName),
        render: (u) => u.fullName || '—',
        tooltip: (u) => u.fullName || '',
      },
      {
        key: 'email',
        header: 'Correo electrónico',
        width: 280,
        render: (u) => u.email || '—',
        tooltip: (u) => u.email || '',
      },
    ],
  },

  permissions: {
    canCreate: (role) => AdminRoles.includes(role),
    canEditOrDelete: (role) => AdminRoles.includes(role),
  },
};

const registry = {
  [Role.Doctor]: doctorConfig,
  [Role.SuperUser]: superUserConfig,
  [Role.AppTester]: appTesterConfig,
};

export const getUserTypeConfig = (userType) => {
  const cfg = registry[userType];
  if (!cfg) {
    throw new Error(`Unknown user type: ${userType}`);
  }
  return cfg;
};

export const getConfigForRole = (role) =>
  Object.values(registry).find((cfg) => cfg.roles.includes(role)) ?? null;
